import { useRef, useCallback, useEffect } from 'react';
import { classifySwipe } from './swipeDetector';

export interface UseSwipeNavOptions {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  /** Minimum horizontal distance (px) to commit a swipe. Default 60. */
  threshold?: number;
  /**
   * Minimum |dy|/|dx| ratio before the gesture is classified as vertical and
   * aborted. Keeps section swipe from hijacking vertical scroll. Default 1.5.
   */
  directionLockRatio?: number;
}

/** How long (ms) the ghost-click suppression window stays active after a committed swipe. */
const SWIPE_CLICK_SUPPRESS_MS = 300;

type GestureState =
  | { phase: 'idle' }
  | { phase: 'tracking'; startX: number; startY: number }
  | { phase: 'locked-h'; startX: number }
  | { phase: 'locked-v' };

export function useSwipeNav({
  onSwipeLeft,
  onSwipeRight,
  threshold = 60,
  directionLockRatio = 1.5,
}: UseSwipeNavOptions) {
  const stateRef = useRef<GestureState>({ phase: 'idle' });
  /** True for SWIPE_CLICK_SUPPRESS_MS after a committed swipe fires. */
  const swipeJustFiredRef = useRef(false);
  const suppressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onPointerDown = useCallback(
    (e: Pick<PointerEvent, 'clientX' | 'clientY' | 'pointerId'> & { currentTarget: { setPointerCapture(id: number): void } }) => {
      if (stateRef.current.phase !== 'idle') return;
      e.currentTarget.setPointerCapture(e.pointerId);
      stateRef.current = { phase: 'tracking', startX: e.clientX, startY: e.clientY };
    },
    [],
  );

  const onPointerMove = useCallback(
    (e: Pick<PointerEvent, 'clientX' | 'clientY'>) => {
      const s = stateRef.current;
      if (s.phase === 'idle' || s.phase === 'locked-v') return;

      if (s.phase === 'tracking') {
        const dx = e.clientX - s.startX;
        const dy = e.clientY - s.startY;
        const direction = classifySwipe({ dx, dy, threshold, directionLockRatio });
        if (direction === 'vertical') {
          stateRef.current = { phase: 'locked-v' };
        } else if (direction === 'left' || direction === 'right') {
          stateRef.current = { phase: 'locked-h', startX: s.startX };
        }
        // 'idle' → still tracking, keep accumulating movement
      }
    },
    [threshold, directionLockRatio],
  );

  const onPointerUp = useCallback(
    (e: Pick<PointerEvent, 'clientX' | 'clientY'>) => {
      const s = stateRef.current;
      stateRef.current = { phase: 'idle' };
      if (s.phase !== 'locked-h') return;

      // Arm ghost-click suppression before firing the callback so that any
      // synchronous click dispatched by the browser during this event cycle is
      // already blocked.
      if (suppressTimerRef.current !== null) clearTimeout(suppressTimerRef.current);
      swipeJustFiredRef.current = true;
      suppressTimerRef.current = setTimeout(() => {
        swipeJustFiredRef.current = false;
        suppressTimerRef.current = null;
      }, SWIPE_CLICK_SUPPRESS_MS);

      const dx = e.clientX - s.startX;
      if (dx < 0) {
        onSwipeLeft();
      } else {
        onSwipeRight();
      }
    },
    [onSwipeLeft, onSwipeRight],
  );

  const onPointerCancel = useCallback(() => {
    stateRef.current = { phase: 'idle' };
  }, []);

  // Clear any pending suppression timer on unmount so stale callbacks don't
  // touch the ref after the component is gone.
  useEffect(() => {
    return () => {
      if (suppressTimerRef.current !== null) {
        clearTimeout(suppressTimerRef.current);
        suppressTimerRef.current = null;
      }
    };
  }, []);

  /**
   * Attach to the swipe container as `onClickCapture`. In React's synthetic
   * capture phase this prevents the ghost-click that browsers fire under the
   * finger after pointerup from reaching React child onClick handlers via the
   * root delegator (React 17+). Native addEventListener('click') on children
   * would still fire — none in this codebase, but worth knowing.
   */
  const onClickCapture = useCallback((e: { preventDefault(): void; stopPropagation(): void }) => {
    if (swipeJustFiredRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClickCapture };
}
